import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';
import usePageTracking from '../hooks/usePageTracking';
import { kisitliTip, kisitliSayfalar } from './RestrictedSidebar';

/**
 * Kısıtlı roldeki markada açılabilen tek sayfa + marka seçici. Sayfa tanımı
 * RestrictedSidebar'daki KISITLI_TIPLER tablosundan gelir — tek kaynak.
 */
const izinliYollar = (tip, brand) => [...kisitliSayfalar(tip, brand).map((s) => s.yol), '/brand-selector'];

// Yataş grubunda MARKETER rolü klasik Overview'u görmez; girişi Performans Panosu (16 Eki 2026).
const YATAS_PANO_BRANDS = [
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7', 'dfcf54dd-b00d-434a-be1e-27b6484848eb',
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f', 'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e',
];
const KLASIK_OVERVIEW_YOLLARI = ['/', '/overview', '/overview2'];
export const yatasPanoZorunlu = (brand) =>
  brand?.currentUserRole === 'MARKETER' && YATAS_PANO_BRANDS.includes(brand?.id);

export default function ProtectedRoute({ children, requireBrand = true, requireSuperuser = false, allowUserIds = null, allowEmails = null, allowBrandIds = null, denyRoles = null }) {
  const { user, loading, selectedBrand } = useAuth();
  const location = useLocation();
  
  // Track page views and user activity for authenticated users
  usePageTracking(!!user && !loading);
  
  console.log('ProtectedRoute:', { 
    user, 
    selectedBrand, 
    currentPath: location.pathname 
  }); // Debug log

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <p className="text-gray-600 text-lg animate-pulse">
            Loading Glowytics...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Explicit allow-list by user id
  if (Array.isArray(allowUserIds) && allowUserIds.length > 0) {
    const userId = user?.id || user?.userId || user?._id;
    if (!userId || !allowUserIds.includes(userId)) {
      return <Navigate to="/" />;
    }
  }

  // Explicit allow-list by email
  if (Array.isArray(allowEmails) && allowEmails.length > 0) {
    const userEmail = user?.email?.toLowerCase();
    // '@alan.com' ile başlayan girdi o alan adındaki herkese izin verir (Sidebar'daki kuralla aynı).
    const izinli = !!userEmail && allowEmails.some(e => {
      const x = e.toLowerCase();
      return x.startsWith('@') ? userEmail.endsWith(x) : x === userEmail;
    });
    if (!izinli) {
      return <Navigate to="/" />;
    }
  }

  // Explicit allow-list by brand id
  if (Array.isArray(allowBrandIds) && allowBrandIds.length > 0) {
    const currentBrandId = selectedBrand?.id || selectedBrand?.brandId;
    if (!currentBrandId || !allowBrandIds.includes(currentBrandId)) {
      return <Navigate to="/" />;
    }
  }

  // Rol engeli: seçili markadaki rol listedeyse (ör. MARKETER) ana sayfaya
  if (Array.isArray(denyRoles) && denyRoles.includes(selectedBrand?.currentUserRole)) {
    return <Navigate to="/" />;
  }

  // Superuser guard (frontend-only; assumes user?.role or user?.isSuperuser)
  if (requireSuperuser) {
    const isSuper = Boolean(user?.isSuperuser || user?.role === 'superuser' || user?.role === 'admin');
    if (!isSuper) {
      return <Navigate to="/" />;
    }
  }

  // Brand seçili değilse ve brand-selector sayfasında değilsek, brand-selector'a yönlendir
  if (requireBrand && !selectedBrand && location.pathname !== '/brand-selector') {
    console.log('Redirecting to brand selector'); // Debug log
    return <Navigate to="/brand-selector" />;
  }

  const tip = kisitliTip(selectedBrand);

  // Brand seçiliyse ve brand-selector sayfasındaysak, overview'a yönlendir
  if (requireBrand && selectedBrand && location.pathname === '/brand-selector') {
    return <Navigate to={tip ? tip.yol : '/overview'} />;
  }

  if (yatasPanoZorunlu(selectedBrand) && KLASIK_OVERVIEW_YOLLARI.includes(location.pathname)) {
    return <Navigate to="/yatas/pano" replace />;
  }

  // 🔒 Kısıtlı rol (FINANCE / GBP): SEÇİLİ MARKADA sadece kendi sayfası açılır.
  // Kullanıcı marka değiştirdiğinde kısıt kalkabilir. Bu bir kolaylık kısıtıdır;
  // asıl yetkilendirme backend'de (middleware/auth.js → utils/hesapKisitlari.js).
  if (tip && !izinliYollar(tip, selectedBrand).includes(location.pathname)) {
    return <Navigate to={tip.yol} replace />;
  }

  return children;
} 